import { Headline } from '@almbrand/headline';
import { Modal } from '@almbrand/modal';
import { RichText } from '@almbrand/richtext';
import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { MobileVariation, setModal } from 'store/slices/modalSlice';
import styles from './ModalComponent.module.scss';

export const ModalComponent = () => {
	const dispatch = useAppDispatch();
	const { displayModal, modalProps, previousFocusElement } = useAppSelector((state) => state.modal);
	const modalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (displayModal) {
			// Set focus on modal
			if (modalRef.current) {
				modalRef.current.focus();
			}
		} else if (previousFocusElement) {
			// Restore focus when modal closes using the stored selector
			const elementToFocus = document.querySelector(previousFocusElement);
			if (elementToFocus instanceof HTMLElement) {
				elementToFocus.focus();
				// Clean up the data attribute if it was added
				if (previousFocusElement.startsWith('[data-focus-restore-')) {
					const attributeStart = previousFocusElement.indexOf('[');
					const attributeEnd = previousFocusElement.indexOf('=');

					if (attributeStart !== -1 && attributeEnd > attributeStart + 1) {
						const attributeName = previousFocusElement.slice(attributeStart + 1, attributeEnd);
						elementToFocus.removeAttribute(attributeName);
					}
				}
			}
		}
	}, [displayModal, previousFocusElement]);

	if (!displayModal || !modalProps) return null;

	const handleClose = () => dispatch(setModal({ displayModal: false }));

	const mobileVariation = (): MobileVariation => {
		if (modalProps.mobileVariation) {
			return modalProps.mobileVariation;
		}
		for (const field of modalProps.fields ?? []) {
			// for now only support select fields, but we can add more conditions for other field types if needed
			if (field?.props?.contentType === 'SelectFieldBlock') {
				return 'fullHeightFullWidth';
			}
		}
		return 'fullWidth';
	};

	const getContent = () => {
		// If content is a string, render it as rich text. If it's a React node, render it directly.
		if (modalProps.content) {
			if (typeof modalProps.content === 'string') {
				return (
					<RichText
						description={modalProps.content}
						className={
							(modalProps.className, modalProps.isErrorModal ? styles.ModalComponent__error : undefined)
						}
					/>
				);
			}
			return modalProps.content;
		}
		return null;
	};

	return (
		<Modal
			mode='default'
			mobileVariation={mobileVariation()}
			{...modalProps}
			handleClose={handleClose}
		>
			{
				<>
					{/* This span is used as a reference point for returning focus to the correct element. The ref cannot reference the following div, as this in some cases gives a focus border around the div text. */}
					<span ref={modalRef} />
					<div
						className={styles.ModalComponent}
						tabIndex={-1}
					>
						{modalProps.subtitle && (
							<div className={styles.ModalComponent__headline}>
								<Headline
									fontColor='primary'
									size='default'
									title={modalProps.subtitle}
									variant='h2'
								/>
							</div>
						)}
						{getContent()}
						{modalProps.fields?.map((field, idx) => <div key={idx + '_'}>{field}</div>)}
					</div>
				</>
			}
		</Modal>
	);
};
